import {
  DUE_REMINDER_OFFSETS,
  type CompletionState,
  type DueBucket,
  type DueReminderOffsetMinutes,
} from '@molt/shared';

/**
 * Mirrors `AttachmentTranslate` (`lib/attachments.ts:179`) and through it
 * `CommentTranslate` (`lib/comments.ts:37-41`) so this module stays decoupled
 * from the interface-language context — a non-component module takes a
 * translator as a parameter rather than importing the React context.
 *
 * The `count?` third parameter is carried even though this module declares no
 * plural: all four sibling declarations have it, and dropping it would make the
 * type structurally incompatible with `InterfaceLanguageContextValue['t']` at
 * every call site.
 */
export type CardDateTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

export type DueStatus =
  'none' | 'neutral' | 'due-soon' | 'overdue' | 'long-overdue' | 'complete';

const DAY_MS = 24 * 60 * 60 * 1000;

const PG_TIMESTAMP_RE =
  /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})[ T](?<hour>\d{2}):(?<minute>\d{2}):(?<second>\d{2})(?:\.(?<fraction>\d+))?(?<offset>Z|[+-]\d{2}(?::?\d{2})?)?$/;

export function parseCardTimestamp(value: string): Date | null {
  const match = PG_TIMESTAMP_RE.exec(value.trim());
  if (!match?.groups) return null;

  const { year, month, day, hour, minute, second, fraction, offset } = match.groups;
  const milliseconds = fraction ? Math.round(Number(`0.${fraction}`) * 1000) : 0;
  let offsetMinutes = 0;
  if (offset !== undefined && offset !== 'Z') {
    const sign = offset.startsWith('-') ? -1 : 1;
    const digits = offset.slice(1).replace(':', '');
    offsetMinutes =
      sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4) || 0));
  }

  const time =
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      milliseconds,
    ) -
    offsetMinutes * 60_000;
  const date = new Date(time);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function dueStatus(input: {
  dueAt: string | null;
  completed: boolean;
  now?: Date;
}): DueStatus {
  if (input.completed) return 'complete';
  if (input.dueAt === null) return 'none';

  const due = parseCardTimestamp(input.dueAt);
  if (due === null) return 'none';

  const now = input.now ?? new Date();
  const diff = due.getTime() - now.getTime();
  if (diff > DAY_MS) return 'neutral';
  if (diff >= 0) return 'due-soon';
  if (diff >= -DAY_MS) return 'overdue';
  return 'long-overdue';
}

/**
 * `t` is required and first: ten production call sites across five components,
 * so a missed one fails `tsc` rather than rendering English forever. An
 * optional parameter with an English default would let every un-migrated caller
 * compile — which is the exact silent failure this workstream exists to remove.
 *
 * The `switch` is kept exhaustive over the union rather than collapsed into a
 * `Record<DueStatus, string>` lookup: both are exhaustive today, but only the
 * `switch` keeps failing to compile if an arm is later given an early return.
 */
export function dueStatusLabel(t: CardDateTranslate, status: DueStatus): string {
  switch (status) {
    case 'none':
      return t('primitives.cardDate.status.none');
    case 'neutral':
      return t('primitives.cardDate.status.neutral');
    case 'due-soon':
      return t('primitives.cardDate.status.dueSoon');
    case 'overdue':
      return t('primitives.cardDate.status.overdue');
    case 'long-overdue':
      return t('primitives.cardDate.status.longOverdue');
    case 'complete':
      return t('primitives.cardDate.status.complete');
  }
}

/**
 * Only the unparseable branch is routed. The `Intl.DateTimeFormat` option
 * values below are API identifiers, not copy — translating one throws a
 * `RangeError`. Its `undefined` locale follows the BROWSER, not the interface
 * language; that divergence is the audit recorded against
 * `formatAttachmentTimestamp`, and this slice does not answer it.
 */
export function formatCardDate(t: CardDateTranslate, value: string): string {
  const parsed = parseCardTimestamp(value);
  if (parsed === null) return t('primitives.cardDate.unknown');
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

/**
 * BE-44.7 (FR-EXP-029 edge case): render a due date that may be date-only.
 * An all-day due is stored midnight-UTC-normalised, so the calendar date is
 * derived from the UTC getters — never the viewer's timezone — and two
 * viewers on opposite sides of the date line read the same day. Timed dues
 * keep `formatCardDate`'s instant-in-viewer-timezone rendering.
 */
export function formatCardDueDate(
  t: CardDateTranslate,
  value: string | null,
  dueAllDay: boolean,
): string | null {
  if (value === null) return null;
  const parsed = parseCardTimestamp(value);
  if (parsed === null) return t('primitives.cardDate.unknown');
  if (!dueAllDay) return formatCardDate(t, value);
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

/**
 * BE-44.7: the calendar-date half of `formatCardDueDate`, as a plain
 * `YYYY-MM-DD` for assertions and scheduling. UTC getters, for the same
 * reason: an all-day instant's calendar date must not depend on who reads it.
 */
export function allDayCalendarDate(value: string): string | null {
  const parsed = parseCardTimestamp(value);
  if (parsed === null) return null;
  const pad = (number: number) => String(number).padStart(2, '0');
  return `${parsed.getUTCFullYear()}-${pad(parsed.getUTCMonth() + 1)}-${pad(parsed.getUTCDate())}`;
}

export function formatDateTimeLocal(value: string | null): string {
  if (value === null) return '';
  const parsed = parseCardTimestamp(value);
  if (parsed === null) return '';
  const pad = (number: number) => String(number).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(
    parsed.getDate(),
  )}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

/**
 * The thrown message stays English on purpose: no user can reach it. Both call
 * sites discard it — `InboxCardPanel.tsx:46-49` catches bare and renders the
 * already-routed `inbox.card.invalidDate`, and `CardChecklistSection.tsx:897`
 * catches bare and clears the date. Routing it would declare a key nothing can
 * render.
 *
 * The operative rule is therefore: **route a throw iff the catch site RENDERS
 * the message.** `putAttachmentBytes` is the contrasting case — its thrown
 * message IS rendered as visible copy, which is why that one was routed, and
 * ⚠ the FE-80.1 card-stickers slice routed `putStickerBytes`' two throws on the
 * same rule, `submitUpload` in `CardStickerSection.tsx` being the catch site
 * that feeds `cause.message` to a visible `<Notice tone="bad">`. An earlier
 * version of this docblock cited `lib/stickers.ts` as settled dead weight; that
 * clause was prose that had drifted from this rule and is removed rather than
 * updated, so it cannot be read as an argument for deleting those keys.
 */
export function dateTimeLocalToIso(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) throw new Error('Choose a valid date and time.');
  return date.toISOString();
}

/**
 * Nine keys, one per offset — not one plural message fed the offset as a count.
 * The nine labels span four nouns (minutes, hour, hours, day, days), and a
 * plural form selects between forms of ONE noun by a count; no count can pick
 * the noun. See the catalogue comment beside `primitives.cardDate.reminder.*`.
 */
export function reminderLabel(
  t: CardDateTranslate,
  value: DueReminderOffsetMinutes | null,
): string {
  switch (value) {
    case null:
      return t('primitives.cardDate.reminder.none');
    case 0:
      return t('primitives.cardDate.reminder.atTime');
    case 5:
      return t('primitives.cardDate.reminder.minutes5');
    case 10:
      return t('primitives.cardDate.reminder.minutes10');
    case 15:
      return t('primitives.cardDate.reminder.minutes15');
    case 60:
      return t('primitives.cardDate.reminder.hours1');
    case 120:
      return t('primitives.cardDate.reminder.hours2');
    case 1440:
      return t('primitives.cardDate.reminder.days1');
    case 2880:
      return t('primitives.cardDate.reminder.days2');
  }
}

export const REMINDER_OPTIONS: readonly (DueReminderOffsetMinutes | null)[] = [
  null,
  ...DUE_REMINDER_OFFSETS,
];

/**
 * ⚠ `dueFilterLabel` and `completionFilterLabel` below have NO importers. They
 * were superseded by the formatter-injecting pair in `lib/board-filter.ts`
 * (`dueBucketFilterLabel`, `completionFilterLabel`), which is what
 * `BoardFilterPanel.tsx` imports and `board-filter.test.ts` tests — the
 * duplicate name is why a grep for either looks live.
 *
 * Their seven English strings are deliberately NOT routed: keys no call site
 * can reach are dead weight. They are equally deliberately NOT deleted, because
 * removing an export is a change to this module's public surface and not a
 * string change. Whoever retires them owns that decision.
 */
export function dueFilterLabel(bucket: DueBucket): string {
  switch (bucket) {
    case 'no_due_date':
      return 'No due date';
    case 'overdue':
      return 'Overdue';
    case 'due_next_day':
      return 'Due in the next day';
    case 'due_next_week':
      return 'Due in the next week';
    case 'due_next_month':
      return 'Due in the next month';
  }
}

export function completionFilterLabel(state: CompletionState): string {
  switch (state) {
    case 'marked_complete':
      return 'Marked as complete';
    case 'not_marked_complete':
      return 'Not marked as complete';
  }
}
